"use client";

import ProductPage from "@/components/ProductPage";
import { KassensystemHardwareSection } from "@/components/KassensystemHardwareSection";

import ImageHighlight from "./highlight.png";
import ImageFeatureEC from "./feature-ec.jpg";
import ImageFeatureCustomers from "./feature-customers.jpg";
import ImageFeatureStock from "./feature-stock.jpg";
import ImageFeatureConsign from "./feature-consign.jpg";
import ImageFeatureOrder from "./feature-order.jpg";
import ImageFeatureExport from "./feature-export.jpg";
import Section from "@/components/Section";
import ImageText from "@/components/ImageText";
import { Check } from "lucide-react";
import SpecialFeaturesSection from "@/components/SpecialFeaturesSection";
import { PricingCard } from "@/components/PricingSection";
import Image from "next/image";

export default function Page() {
  return (
    <ProductPage
      productName="Cashtex Profi – Kassensystem für Second-Hand-Läden"
      hero={{
        title: "Cashtex Profi – Kassensystem für Second-Hand-Läden",
        subtitle:
          "Speziell entwickelt für nachhaltigen Second-Hand-Verkauf: Provisionen, Kommission, Regalvermietung und schnelle Artikelmodellierung – alles in einem System.",
        buttonLeft: {
          title: "Unverbindliche Anfrage",
          href: "/support",
        },
        buttonRight: { title: "Preise", href: "/preise" },
        image: ImageHighlight,
        imageAlt: "Cashtex Profi Second-Hand Kassensystem",
      }}
      highlight={{
        image: ImageHighlight,
        bullets: [
          <>
            <strong>Kommission & Provisionen</strong> automatisch abrechnen –
            transparent für Einlieferer.
          </>,
          <>
            <strong>Regalvermietung</strong> inkl. Mietmodelle, Abrechnung und
            Übersicht.
          </>,
          <>
            <strong>Schnelle Artikelmodellierung</strong> inkl. Etikettendruck
            direkt aus der Erfassung.
          </>,
          <>
            <strong>
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary underline hover:opacity-80"
              >
                GoBD
              </a>
              /
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary underline hover:opacity-80"
              >
                GDPdU
              </a>
              -konform
            </strong>{" "}
            inkl. Kassenbuch und{" "}
            <a
              href="https://www.softguide.de/funktion/datev-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              DATEV
            </a>
            /
            <a
              href="https://www.softguide.de/funktion/fibu-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              FIBU-Export
            </a>
            /
            <a
              href="https://www.seeburger.com/de/ressourcen/wissenswertes/was-ist-edi"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              EDI-Schnittstelle
            </a>
            /
            <a
              href="https://www.bsi.bund.de/DE/Themen/Unternehmen-und-Organisationen/Standards-und-Zertifizierung/Schutz-vor-Manipulation-an-digitalen-Grundaufzeichnungen/Fragen-und-Antworten/fragen-und-antworten_node.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              TSE
            </a>
            -konform/Schnittstelle zu Ihrem Steuerberater.
          </>,
          <>
            <strong>Finanzamt- und Gesetzkonform</strong>
          </>,
        ],
      }}
      beforeVideo={
        <Section variant="transparent" className="py-4 md:py-6 mt-12">
          <ImageText
            imagePosition="right"
            imageSlot={
              <div className="relative overflow-hidden mt-15 rounded-2xl border border-black/10 bg-black shadow-sm h-100">
                <video className="h-full w-full object-cover" controls>
                  <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                </video>
              </div>
            }
          >
            <div className="flex flex-col gap-2 mb-2">
              <Image
                src="/CTXr.svg"
                alt="Cashtex"
                width={120}
                height={36}
                className="object-contain"
              />
              <h2 className="text-3xl md:text-4xl font-semibold text-[#050a18]">
                Kassensystem für Second-Hand-Läden
              </h2>
            </div>

            <p className="text-slate-700 leading-relaxed">
              Cashtex unterstützt Second-Hand-Läden bei der Verwaltung von
              Einlieferungen, Provisionen und Verkaufsprozessen – einfach,
              transparent und effizient.
            </p>

            <p className="mt-2 text-slate-700 leading-relaxed">
              Durch flexible Provisionsmodelle, Regalvermietung und schnelle
              Artikelerfassung behalten Sie jederzeit den Überblick über alle
              Abläufe.
            </p>

            <ul className="mt-2 space-y-2">
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Flexible Provisionen:</strong> Automatische Abrechnung
                  für Einlieferer und Verkäufer.
                </span>
              </li>

              <li className="flex items-start gap-3 text-slate-700">
                <Check className="mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Regalvermietung:</strong> Mietmodelle einfach
                  verwalten und abrechnen.
                </span>
              </li>

              <li className="flex items-start gap-3 text-slate-700">
                <Check className="mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Schnelle Erfassung:</strong> Artikel modellieren und
                  direkt etikettieren.
                </span>
              </li>
            </ul>
          </ImageText>

          <SpecialFeaturesSection
            industries={[
              {
                title: "Second Hand / Kommissionshandel",
                standard: [
                  "Kassensystem & Verkauf",
                  "Kunden- & Lieferantenverwaltung",
                  "Artikel- & Lagerverwaltung",
                  "Berichte & Auswertungen",
                  "Zahlungsarten & Belegausgabe",
                  "Rabatte & Aktionspreise",
                  "Tagesabschluss",
                  "Mehrwertsteuer-Verwaltung",
                ],
                special: [
                  "Kommissionsabrechnung für Lieferanten direkt im System integriert",
                  "Automatische Abrechnungen und Auszahlungen ohne manuelle Berechnung",
                  "Verwaltung und Abrechnung von Regalflächen und Mietverträgen",
                  "Jeder Artikel als Einzelstück mit individueller Preis- und Zustandslogik",
                  "Etiketten- und Barcode-Druck direkt aus der Artikelerfassung",
                  "Unterstützung von Spezialbarcodes (z. B. Gewicht oder individuelle Kennzeichnung)",
                  "Zustandsbewertung und Kategorisierung gebrauchter Artikel",
                  "Übersicht offener Kommissionsabrechnungen pro Einlieferer",
                ],
              },
            ]}
          />
        </Section>
      }
      faqs={[
        {
          q: "Kann Cashtex Provisionen für Verkäufe durch andere verwalten?",
          a: "Ja. Sie können Provisionen flexibel vergeben – z. B. für externe Verkäufer, die Ware über Ihren Laden veräußern. Abrechnungen erfolgen automatisch ohne manuelle Berechnung.",
        },
        {
          q: "Wie wird Artikelmodellierung unterstützt?",
          a: "Mit der Artikelmodellierung erfassen Sie Artikel besonders schnell und können Etiketten direkt daraus drucken – ideal für eine große Anzahl an Einzelstücken.",
        },
        {
          q: "Kann ich Mietverträge für Regalvermietung abrechnen?",
          a: "Ja. Sie können Regalflächen vermieten und die Mietmodelle einfach über Cashtex verwalten und abrechnen – übersichtlich und ohne externen Verwaltungsaufwand.",
        },
        {
          q: "Welche Statistiken und Berichte sind verfügbar?",
          a: "Umsätze, Tagesvergleiche, Renner-/Penner-Listen sowie Auswertungen nach Lieferanten, Artikeln oder Größen – für fundierte Entscheidungen auf einen Blick.",
        },
        {
          q: "Wie starte ich mit der Demoversion?",
          a: "Einfach unverbindlich anfragen – wir melden uns zeitnah mit einem Terminvorschlag und richten die Demo individuell für Sie ein.",
        },
        {
          q: "Unterstützt Cashtex die Verwaltung von Einzelstücken mit individuellem Preis?",
          a: "Ja, jeder Artikel kann als Einzelstück mit eigenem Preis, Zustand und Barcode erfasst werden – besonders praktisch für Second-Hand- und Kommissionsware.",
        },
        {
          q: "Kann ich Kommissionsabrechnungen für Einlieferer erstellen?",
          a: "Ja, Cashtex erstellt Kommissionsabrechnungen automatisch pro Einlieferer – inklusive Übersicht über offene und abgerechnete Posten.",
        },
        {
          q: "Wie läuft die Einrichtung und Schulung ab?",
          a: "Nach einer persönlichen Beratung richtet unser Team das System für Sie ein. Schulungen und Support sind im Einführungsprozess inbegriffen.",
        },
      ]}
      features={[
        {
          src: ImageFeatureEC,
          alt: "Cashtex Second-Hand – schneller Verkauf",
          title: "Schneller Verkauf & Kassenprozess",
          desc: "Quittungen, Retouren, Gutschriften und Belegdruck – ideal für schnelle Second-Hand-Abläufe.",
        },
        {
          src: ImageFeatureCustomers,
          alt: "Cashtex Second-Hand – Kunden und Einlieferer verwalten",
          title: "Kunden- & Einliefererverwaltung",
          desc: "Kundenkarten, Bonusprogramme und zentrale Verwaltung von Einlieferern und Lieferanten.",
        },
        {
          src: ImageFeatureStock,
          alt: "Cashtex Second-Hand – Artikel und Etiketten",
          title: "Artikel- & Lagerverwaltung",
          desc: "Artikelstämme, Kategorien, Barcode- & Etikettendruck sowie einfache Inventuren via Scanner.",
        },
        {
          src: ImageFeatureConsign,
          alt: "Cashtex Second-Hand – Kommission und Konsignation",
          title: "Kommission & Provisionen",
          desc: "Einlieferungen abbilden, automatische Abrechnung und transparente Provisionsmodelle.",
        },
        {
          src: ImageFeatureOrder,
          alt: "Cashtex Second-Hand – Ankauf und Wareneingang",
          title: "Ankauf & Wareneingang",
          desc: "Ankäufe und Lieferungen erfassen – mit direkter Zuordnung und sauberer Dokumentation.",
        },
        {
          src: ImageFeatureExport,
          alt: "Cashtex Second-Hand – Kassenbuch und Export",
          title: "Kassenbuch & Export",
          desc: "Einnahmen/Ausgaben, revisionssicheres Kassenbuch und Exporte für Steuerberater-Systeme.",
        },
      ]}
    >
      {/* PRICING CARDS */}
      <Section className="pb-24 bg-white border-y border-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
          <PricingCard
            title="Leasing ab"
            priceFull="24"
            priceCents="90"
            details="Basierend auf 0,83 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Angebote, Order & Rechnungen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Mieten ab"
            priceFull="48"
            priceCents="90"
            details="Basierend auf 1,63 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Ideal für wachsende Unternehmen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Ratenkauf ab"
            priceFull="74"
            priceCents="90"
            details="Basierend auf 2,49 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit. Restwert 10 %"
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Eigentumserwerb nach Laufzeit",
              "1 Benutzer",
            ]}
          />
        </div>

        <div className="text-center mt-12 text-sm font-bold text-gray-900 tracking-widest uppercase">
          Alle Preise exkl. MwSt.
        </div>
      </Section>
      <KassensystemHardwareSection />
      <Section variant="gray1" className="tse-section">
        <ImageText
          imagePosition="left"
          imageSrc="/tse.png"
          imageAlt="Softwareentwicklung"
          title="TSE & Rechtssicherheit: Wir machen es einfach!"
        >
          <p>
            TSE-Pflicht oder neue Anforderungen vom Finanzamt? Kein Problem –
            wir kennen uns aus und übernehmen die Umsetzung der Technischen
            Sicherheitseinrichtung für Dich und informieren Dich über alle
            wichtigen Änderungen.
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Kein zusätzlicher Aufwand:</strong> Regelmäßige und
              kostenlose Updates
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary " />{" "}
              <strong>Rechtssicher:</strong> TSE-konform – erfüllt KassenSichV
              und GoBD
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Zuverlässig:</strong> Datensicherheit auf deutschen
              Servern und nach höchsten Standards
            </li>
          </ul>
          <p>
            Mit der Kassensoftware von SDI arbeitest Du immer 100%
            finanzamtkonform und bist automatisch auf der sicheren Seite.
          </p>
        </ImageText>
      </Section>
    </ProductPage>
  );
}
